// Module 3 — Closings.
// Route: /reinsurance/closings
//
// Binding, documentation and completion.
//
// One row per SIGNED LINE, not per deal: a deal placed across six markets
// closes six times, on six different days, against six sets of documents.
// The row id is therefore "<dealId>:<marketId>" — a closing is a line on a
// deal and has no record of its own.

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import ClosingDrawer from './ClosingDrawer';
import {
  C, Icons, Kpi, Pill, ProgressCell,
  PrimaryButton, GhostButton, TwoLine,
  money0, date,
} from './kit';

// Premium confirmed by the market. Three states, not two: null means nobody
// has asked yet, and rendering that as "No" would report every untouched
// closing as a refusal.
function ConfirmedPill({ value }) {
  if (value === null || value === undefined) {
    return <span style={{ color: C.faint, fontSize: 12.5 }}>—</span>;
  }
  const yes = value === true;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 11px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 700,
      background: yes ? C.greenBg : C.redBg,
      color: yes ? C.green : C.red,
    }}>
      {yes ? 'Yes' : 'No'}
    </span>
  );
}

export default function ClosingsList() {
  const history = useHistory();
  const { t } = useTranslation();

  const columns = [
    { key: 'reference', label: t('screens.reClosings.colRef', 'Closing ref'), width: 150,
      render: (r) => <span style={{ fontWeight: 600, color: C.ink }}>{r.reference}</span> },
    { key: 'cedantName', label: t('screens.reClosings.colCedant', 'Cedant / client'),
      render: (r) => <TwoLine primary={r.cedantName} secondary={r.dealNumber} /> },
    { key: 'counterpartyName', label: t('screens.reClosings.colCounterparty', 'Counterparty'),
      render: (r) => r.counterpartyName || '—' },
    { key: 'section', label: t('screens.reClosings.colSection', 'Section'),
      render: (r) => r.section || '—' },
    { key: 'inceptionDate', label: t('screens.reClosings.colInception', 'Inception date'), width: 116,
      render: (r) => date(r.inceptionDate) },
    { key: 'signedAmount', label: t('screens.reClosings.colSigned', 'Signed lines'), numeric: true,
      render: (r) => (r.signedAmount === null
        ? <span style={{ color: C.faint }}>—</span>
        : money0(r.signedAmount, r.currency)) },
    { key: 'documentProgress', label: t('screens.reClosings.colDocs', 'Doc progress'), width: 92,
      render: (r) => <ProgressCell done={r.documentProgress?.done || 0} total={r.documentProgress?.total || 0} /> },
    { key: 'premiumConfirmed', label: t('screens.reClosings.colPremium', 'Premium confirmed'), width: 120,
      render: (r) => <ConfirmedPill value={r.premiumConfirmed} /> },
    { key: 'status', label: t('screens.reClosings.colStatus', 'Status'), width: 140,
      render: (r) => <Pill status={r.status} /> },
  ];

  return (
    <ModulePage
      title={t('screens.reClosings.title', 'Closings')}
      subtitle={t('screens.reClosings.subtitle', 'Binding, documentation and completion')}
      endpoint='/reinsurance/closings'
      searchPlaceholder={t('screens.reClosings.search', 'Search closings, cedants, counterparties…')}
      actions={(
        <>
          {/* A closing is not created from nothing: it IS a signed market
              line on a bound placement, and it appears here the moment the
              line is signed. "Create closing" was a button with no handler
              promising something the domain does not have. It points at the
              placements that produce closings instead — and the memo and the
              notes are raised per closing, in its drawer, where the closing
              is. */}
          <PrimaryButton icon={false} onClick={() => history.push('/reinsurance/placement?placementStatus=bound')}>
            {t('screens.reClosings.fromPlacement', 'Bound placements')}
          </PrimaryButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.doc()} tone='blue'
            value={s?.awaitingSignature?.value ?? '—'} trend={s?.awaitingSignature?.trend}
            label={t('screens.reClosings.kpiAwaiting', 'Contracts awaiting signature')} />
          <Kpi icon={Icons.check()} tone='green'
            value={s?.boundThisMonth?.value ?? '—'} trend={s?.boundThisMonth?.trend}
            label={t('screens.reClosings.kpiBound', 'Bound this month')} />
          <Kpi icon={Icons.clip()} tone='amber'
            value={s?.outstandingDocuments?.value ?? '—'} trend={s?.outstandingDocuments?.trend}
            comparison={s?.outstandingDocuments?.comparison}
            label={t('screens.reClosings.kpiDocs', 'Outstanding documents')} />
          <Kpi icon={Icons.coins()} tone='red'
            value={s?.premiumAdvicePending?.value ?? '—'} trend={s?.premiumAdvicePending?.trend}
            label={t('screens.reClosings.kpiPremium', 'Premium advice pending')} />
        </>
      )}
      filters={[
        { key: 'status', label: 'Status', options: [
          { value: 'all', label: 'All statuses' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'awaiting_signature', label: 'Awaiting Signature' },
          { value: 'pending_docs', label: 'Pending Docs' },
          { value: 'closed', label: 'Closed' },
          { value: 'cancelled', label: 'Cancelled' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reClosings.empty', 'No closings. A closing appears here once a market signs its line.')}
      drawerTabs={['Overview', 'Documentation', 'Financials', 'Activity']}
      drawerHeader={(r) => ({
        title: r.reference,
        badge: <Pill status={r.status} />,
        lines: [r.cedantName, [r.section, date(r.inceptionDate)].filter(Boolean).join(' · ')].filter(Boolean),
      })}
      drawer={(r, { tab, reload }) => (
        <ClosingDrawer row={r} tab={tab} reload={reload} />
      )}
    />
  );
}
